import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SwiperCore from 'swiper';
import SwiperBundle from 'swiper';
import { A11y, Autoplay, EffectFade, Keyboard, Navigation, Pagination, Scrollbar, Virtual } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';
import { HeadlineAnimation, HiddenTitle, InitAnimation, Root, TitlePlaceholder, TitlePlaceholderWrapper, } from './styles';
import Teaser from '../Teaser/Teaser';
import SwiperButton from '../Swiper/SwiperButton/SwiperButton';
import getResponsiveSwiperParams, { SwiperBreakpoint } from './getResponsiveSwiperParams';
import { THEME_LAYOUT_TYPES } from '../../types';
import Hidden from '../primitives/Hidden';
import { Placeholder } from '../primitives/Placeholder';
import { cx } from '@linaria/core';
import { Typo } from '../primitives/Typo';
import { useTheme } from '../ThemeProvider/ThemeProvider';
SwiperCore.use([A11y, Autoplay, EffectFade, Keyboard, Navigation, Pagination, Scrollbar, Virtual]);
function onInteraction(setUserInteraction) {
    setUserInteraction(true);
}
function getParams(stageRatio, hasAutoplay, setUserInteraction) {
    return {
        stage: {
            speed: 300,
            spaceBetween: 0,
            loop: true,
            loopedSlides: stageRatio === 'default' ? 2 : 5,
            loopAdditionalSlides: 0,
            autoplay: hasAutoplay ? { delay: 5000 } : false,
            centeredSlides: true,
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: false,
                dynamicBullets: true,
                dynamicMainBullets: 3,
            },
            on: {
                click() {
                    onInteraction(setUserInteraction);
                },
                touchStart() {
                    onInteraction(setUserInteraction);
                },
            },
        },
    };
}
function changeTeaserRatio(swiper, items, teaserRatio, setTeaserRatio, ratio) {
    if (swiper && swiper.slides) {
        const slides = swiper.slides;
        let slideRatio;
        for (const key in slides) {
            const slide = slides[key];
            if (slide.classList) {
                const slideIndex = slide.dataset.swiperSlideIndex;
                const slideImg = slide.querySelector('img');
                const itemsImg = items[slideIndex] && items[slideIndex].image;
                let currentRatio = `aspect16x7`;
                if (window.innerWidth <= SwiperBreakpoint.EXTRA_SMALL) {
                    slideRatio = '1x1';
                    currentRatio = `aspect1x1`;
                }
                else if (window.innerWidth <= SwiperBreakpoint.LARGE) {
                    slideRatio = '16x9';
                    currentRatio = `aspect16x9`;
                }
                else {
                    slideRatio = '16x7';
                    currentRatio = `aspect16x7`;
                }
                let currentSrc = null;
                let currentSrcSet = null;
                let currentSizes = null;
                let currentAlt = null;
                let currentTitle = null;
                // Check if we have values for current ratio
                if (itemsImg && itemsImg[currentRatio]) {
                    currentSrc = itemsImg[currentRatio].src;
                    currentSrcSet = itemsImg[currentRatio].srcset;
                    currentSizes = itemsImg[currentRatio].sizes;
                    currentAlt = itemsImg[currentRatio].alt;
                    currentTitle = itemsImg[currentRatio].title;
                }
                // Check for 16x7 Fallback
                else if (itemsImg && itemsImg.aspect16x7) {
                    currentSrc = itemsImg.aspect16x7.src;
                    currentSrcSet = itemsImg.aspect16x7.srcset;
                    currentSizes = itemsImg.aspect16x7.sizes;
                    currentAlt = itemsImg.aspect16x7.alt;
                    currentTitle = itemsImg.aspect16x7.title;
                }
                // Check for 16x9 Fallback
                else if (itemsImg && itemsImg.aspect16x9) {
                    currentSrc = itemsImg.aspect16x9.src;
                    currentSrcSet = itemsImg.aspect16x9.srcset;
                    currentSizes = itemsImg.aspect16x9.sizes;
                    currentAlt = itemsImg.aspect16x9.alt;
                    currentTitle = itemsImg.aspect16x9.title;
                }
                // Check if there is any other ratio we could use as Fallback
                else {
                    for (const item in itemsImg) {
                        if (itemsImg && itemsImg[item]) {
                            currentSrc = itemsImg[item].src;
                            currentSrcSet = itemsImg[item].srcset;
                            currentSizes = itemsImg[item].sizes;
                            currentAlt = itemsImg[item].alt;
                            currentTitle = itemsImg[item].title;
                        }
                    }
                }
                if (slideImg) {
                    slideImg.src = currentSrc ? currentSrc : slideImg.src;
                    slideImg.srcset = currentSrcSet ? currentSrcSet : slideImg.srcset;
                    slideImg.sizes = currentSizes ? currentSizes : slideImg.sizes;
                    slideImg.alt = currentAlt ? currentAlt : slideImg.alt;
                    slideImg.title = currentTitle ? currentTitle : slideImg.title;
                }
            }
        }
        setTeaserRatio(slideRatio);
    }
    else if (ratio && ratio !== teaserRatio) {
        setTeaserRatio(ratio);
    }
}
function swiperUpdate(swiper, items, teaserRatio, setTeaserRatio, ratio) {
    if (swiper) {
        swiper.update();
        changeTeaserRatio(swiper, items, teaserRatio, setTeaserRatio, ratio);
    }
}
function useMouseEnter(swiper, hasAutoplay) {
    return useCallback(() => {
        if (swiper && hasAutoplay) {
            swiper.autoplay.stop();
        }
    }, [swiper]);
}
function useMouseLeave(swiper, hasAutoplay, hasUserInteraction) {
    return useCallback(() => {
        if (swiper && !hasUserInteraction && hasAutoplay) {
            swiper.autoplay.start();
        }
    }, [swiper, hasUserInteraction]);
}
function useFocusIn(swiper, hasAutoplay) {
    return useCallback(() => {
        if (swiper) {
            swiper.keyboard.enable();
            if (hasAutoplay) {
                swiper.autoplay.stop();
            }
        }
    }, [swiper]);
}
function useFocusOut(swiper, hasAutoplay) {
    return useCallback(() => {
        if (swiper) {
            swiper.keyboard.disable();
            if (hasAutoplay) {
                swiper.autoplay.start();
            }
        }
    }, [swiper]);
}
function setEventListeners(swiper, onFocusIn, onFocusOut, onMouseEnter, onMouseLeave) {
    if (swiper && !swiper.destroyed) {
        swiper.el.addEventListener('focusin', onFocusIn);
        swiper.el.addEventListener('focusout', onFocusOut);
        swiper.on('beforeDestroy', () => {
            swiper.el.removeEventListener('focusin', onFocusIn);
            swiper.el.removeEventListener('focusOut', onFocusOut);
        });
        swiper.el.addEventListener('mouseenter', onMouseEnter);
        swiper.el.addEventListener('mouseleave', onMouseLeave);
        swiper.on('beforeDestroy', () => {
            swiper.el.removeEventListener('mouseenter', onMouseEnter);
            swiper.el.removeEventListener('mouseleave', onMouseLeave);
        });
    }
    return function cleanup() {
        if (swiper && !swiper.destroyed) {
            swiper.el.removeEventListener('mouseenter', onMouseEnter);
            swiper.el.removeEventListener('mouseleave', onMouseLeave);
            swiper.el.removeEventListener('focusin', onFocusIn);
            swiper.el.removeEventListener('focusOut', onFocusOut);
        }
    };
}
function setLoading(isLoading, setLoaded) {
    if (isLoading) {
        const globalLoadedTimeout = setTimeout(() => {
            setLoaded(true);
        }, 1000);
        return () => {
            clearTimeout(globalLoadedTimeout);
        };
    }
    else {
        setLoaded(true);
    }
}
function getTeasers(items, TeaserComponent, hasDisabledAnimations, isLoading, stageRatio, teaserRatio, hoverEffect, mountedSlides) {
    return items.map((item, i) => {
        if (i <= 1 || i >= items.length - 2) {
            item.isLoadingEagerly = true;
        }
        const onTeaserVisible = (data) => {
            if (item.onVisible && !mountedSlides.includes(i)) {
                item.onVisible(data);
                mountedSlides.push(i);
            }
        };
        return (React.createElement(SwiperSlide, { key: i },
            React.createElement(TeaserComponent, { ...item, title: item.longTitle || item.title, ratio: stageRatio !== 'default' ? stageRatio : teaserRatio, onVisible: onTeaserVisible, index: i, hasDisabledAnimations: hasDisabledAnimations, isLoading: isLoading, hoverEffect: hoverEffect })));
    });
}
function setAnimations(swiperWrapperRef, hasDisabledAnimations) {
    if (swiperWrapperRef.current && !hasDisabledAnimations) {
        swiperWrapperRef.current.style.willChange = 'transform, opacity, contents';
        const globalWillChangeTimeout = setTimeout(() => {
            if (swiperWrapperRef.current) {
                swiperWrapperRef.current.style.willChange = 'auto';
            }
        }, 2000);
        return () => {
            clearTimeout(globalWillChangeTimeout);
        };
    }
}
function useSlidesForDom(items, itemAs, hasDisabledAnimations, isLoading, stageRatio, teaserRatio, hoverEffect, mountedSlides) {
    const TeaserComponent = itemAs;
    return useMemo(() => {
        return getTeasers(items, TeaserComponent, hasDisabledAnimations, isLoading, stageRatio, teaserRatio, hoverEffect, mountedSlides);
    }, [isLoading, items, stageRatio, teaserRatio]);
}
export function ClassicStage({ title, hasVisibleTitle = true, titleLink, hasAutoplay = true, ratio = '16x9', stageRatio = 'default', hoverEffect = 'default', items, hasDisabledAnimations = false, itemAs = Teaser, isLoading = false, }) {
    /** Variables */
    const { layout } = useTheme();
    const [teaserRatio, setTeaserRatio] = useState(ratio ? ratio : '16x9');
    const [swiper, updateSwiper] = useState(null);
    const [hasUserInteraction, setUserInteraction] = useState(false);
    const [hasLoaded, setLoaded] = useState(false);
    const mountedSlidesRef = useRef([]);
    const swiperWrapperRef = useRef(null);
    const swiperContainerRef = useRef(null);
    const typeParams = getParams(stageRatio, hasAutoplay, setUserInteraction);
    const onMouseEnter = useMouseEnter(swiper, hasAutoplay);
    const onMouseLeave = useMouseLeave(swiper, hasAutoplay, hasUserInteraction);
    const onFocusIn = useFocusIn(swiper, hasAutoplay);
    const onFocusOut = useFocusOut(swiper, hasAutoplay);
    const [responsiveSwiperParams, key] = getResponsiveSwiperParams(stageRatio);
    const slides = useSlidesForDom(items, itemAs, hasDisabledAnimations, isLoading, stageRatio, teaserRatio, hoverEffect, mountedSlidesRef.current);
    /** Construktor-Hook & Effect-Hooks */
    useEffect(() => {
        swiperUpdate(swiper, items, teaserRatio, setTeaserRatio, ratio);
    }, [swiper, items]);
    useEffect(() => {
        setEventListeners(swiper, onFocusIn, onFocusOut, onMouseEnter, onMouseLeave);
    }, [swiper, onFocusIn, onFocusOut, onMouseEnter, onMouseLeave]);
    useEffect(() => {
        setLoading(isLoading, setLoaded);
    }, [isLoading]);
    useEffect(() => {
        setAnimations(swiperWrapperRef, hasDisabledAnimations);
    }, []);
    useEffect(() => {
        if (!swiperContainerRef.current) {
            console.error('cannot render swiper in empty div!');
            return;
        }
        const swiperBundle = new SwiperBundle(swiperContainerRef.current, {
            keyboard: { enabled: false },
            roundLengths: true,
            a11y: {
                firstSlideMessage: 'Erster Eintrag',
                lastSlideMessage: 'Letzter Eintrag',
                prevSlideMessage: 'Vorheriger Eintrag',
                nextSlideMessage: 'Nächster Eintrag',
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            threshold: 20,
            ...responsiveSwiperParams,
            ...typeParams['stage'],
        });
        swiperBundle.on('resize', (swiper) => {
            changeTeaserRatio(swiper, items, teaserRatio, setTeaserRatio, ratio);
        });
        swiperBundle.on('orientationchange', (swiper) => {
            changeTeaserRatio(swiper, items, teaserRatio, setTeaserRatio, ratio);
        });
        updateSwiper(swiperBundle);
        return () => swiperBundle.destroy();
    }, [ratio]);
    /** JSX */
    return (React.createElement("section", { className: cx(Root.base, Root.stageRatio[stageRatio], Root.hoverEffect[hoverEffect], layout === THEME_LAYOUT_TYPES.ADULT && Root.adult) },
        title && hasVisibleTitle && !titleLink && (React.createElement("span", { className: cx(!hasDisabledAnimations && hasLoaded && HeadlineAnimation.base) },
            React.createElement("h1", { className: cx(Typo.base, Typo.heading03) }, title))),
        title && hasVisibleTitle && titleLink && (React.createElement("a", { href: titleLink },
            React.createElement("span", { className: cx(!hasDisabledAnimations && hasLoaded && HeadlineAnimation.base) },
                React.createElement("h1", { className: cx(Typo.base, Typo.heading03) }, title)))),
        title && !hasVisibleTitle && (React.createElement("span", { className: cx(Hidden.base) },
            React.createElement("h1", { className: cx(Typo.base, Typo.heading03) }, title))),
        !title && hasVisibleTitle && isLoading && (React.createElement("span", { className: cx(!hasDisabledAnimations && hasLoaded && HeadlineAnimation.base) },
            React.createElement("h2", { className: cx(Typo.base, TitlePlaceholderWrapper.base, Typo.heading03) },
                React.createElement("span", { className: cx(Placeholder.base, TitlePlaceholder.base) }),
                React.createElement("span", { className: cx(HiddenTitle.base) }, "Wird geladen...")))),
        items.length > 0 ? (React.createElement(React.Fragment, null,
            React.createElement("span", { className: cx(Hidden.base) },
                "Liste mit ",
                items.length,
                " Eintr\u00E4gen"),
            React.createElement("div", { className: cx(!hasDisabledAnimations && hasLoaded && InitAnimation.base), ref: swiperWrapperRef },
                React.createElement("div", { className: "swiper", ref: swiperContainerRef, key: key },
                    React.createElement("div", { className: "swiper-wrapper" }, slides),
                    React.createElement("div", { className: "swiper-scrollbar" }),
                    React.createElement("div", { className: "swiper-pagination" }),
                    React.createElement(SwiperButton, { direction: "right" }),
                    React.createElement(SwiperButton, { direction: "left" }))))) : (React.createElement("h2", { className: cx(Typo.base, Typo.heading03) }, "Keine Inhalte verf\u00FCgbar"))));
}
export default React.memo(ClassicStage);
